import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useBusiness } from "../contexts/BusinessContext";
import {
  FileText, Plus, Edit2, Trash2, Save, X, DollarSign, Calendar,
  Download, Eye, User, Truck, Clock, Calculator, Search, Filter,
  CheckCircle, XCircle, AlertCircle, Loader2, ChevronDown, ChevronUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Nombres de periodos en español
const PERIOD_NAMES = {
  day: "Día",
  week: "Semana", 
  month: "Mes"
};

const STATUS_CONFIG = {
  draft: { label: "Borrador", color: "bg-slate-600", icon: FileText },
  sent: { label: "Enviada", color: "bg-blue-600", icon: CheckCircle },
  approved: { label: "Aprobada", color: "bg-green-600", icon: CheckCircle },
  rejected: { label: "Rechazada", color: "bg-red-600", icon: XCircle },
  expired: { label: "Expirada", color: "bg-orange-600", icon: AlertCircle }
};

// Términos y condiciones predeterminados para arrendamiento - COMPACTOS
const DEFAULT_RENTAL_TERMS = `CONDICIONES DE ARRENDAMIENTO:
1. DEPÓSITO: 1 mes de renta como garantía, reembolsable al término previa inspección.
2. MANTENIMIENTOS MENORES: Por cuenta del arrendatario (aceite, filtros, engrasado, limpieza).
3. MANTENIMIENTOS MAYORES: Por cuenta del arrendador.
4. DAÑOS: El arrendatario es responsable de daños por mal uso o negligencia.
5. COMBUSTIBLE: Por cuenta del arrendatario.
6. OPERADOR: Si incluye, el arrendatario proporciona condiciones seguras de trabajo.
7. HORARIO: 8 horas diarias, turno diurno. Horas extras se cobran aparte.
8. ENTREGA: El equipo se entrega en óptimas condiciones y debe devolverse igual.
9. VIGENCIA: 15 días naturales a partir de la fecha de emisión.`;

function RentalQuotationsTab() {
  const { businessId } = useBusiness();
  
  // State
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedQuotation, setExpandedQuotation] = useState(null);
  
  // Reference data
  const [clients, setClients] = useState([]);
  const [rentalVehicles, setRentalVehicles] = useState([]);
  const [operators, setOperators] = useState([]);
  const [freightZones, setFreightZones] = useState([]);
  const [companyConfig, setCompanyConfig] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: "",
    client_name: "",
    client_company: "",
    client_rfc: "",
    client_email: "",
    client_phone: "",
    client_address: "",
    items: [],
    rental_start_date: "",
    rental_end_date: "",
    validity_days: 15,
    terms: DEFAULT_RENTAL_TERMS,
    notes: "",
    seller_id: "",
    seller_name: ""
  });
  
  // New item form
  const [newItem, setNewItem] = useState({
    vehicle_id: "",
    vehicle_code: "",
    vehicle_name: "",
    vehicle_brand: "",
    monthly_price: 0,
    period_type: "month",
    period_quantity: 1,
    include_operator: false,
    operator_id: "",
    operator_name: "",
    operator_daily_cost: 0,
    // Flete por equipo
    include_freight: false,
    freight_zone_id: "",
    freight_zone_name: "",
    freight_cost: 0,
    freight_destination: ""
  });
  
  // Load data on mount
  useEffect(() => {
    if (businessId) {
      fetchQuotations();
      fetchClients();
      fetchRentalVehicles();
      fetchOperators();
      fetchFreightZones();
      fetchCompanyConfig();
    }
  }, [businessId]);
  
  const fetchCompanyConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/company-config/${businessId}`);
      if (response.data.success) {
        setCompanyConfig(response.data.config);
      }
    } catch (error) {
      console.error("Error fetching company config:", error);
    }
  };
  
  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/rental-quotations?business_id=${businessId}`);
      if (response.data.success) {
        setQuotations(response.data.quotations);
      }
    } catch (error) {
      toast.error("Error al cargar cotizaciones de renta");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/api/clients`);  // Cargar TODOS los clientes
      if (response.data.success) {
        setClients(response.data.clients);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };
  
  const fetchRentalVehicles = async () => {
    try {
      const response = await axios.get(`${API}/api/rental-vehicles?business_id=${businessId}`);
      if (response.data.success) {
        setRentalVehicles(response.data.vehicles);
      }
    } catch (error) {
      console.error("Error fetching rental vehicles:", error);
    }
  };
  
  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/api/operators?business_id=${businessId}`);
      if (response.data.success) {
        setOperators(response.data.operators);
      }
    } catch (error) {
      console.error("Error fetching operators:", error);
    }
  };
  
  const fetchFreightZones = async () => {
    try {
      const response = await axios.get(`${API}/api/freight-zones?business_id=${businessId}`);
      if (response.data.success) {
        console.log("Freight zones loaded:", response.data.zones);
        setFreightZones(response.data.zones);
      }
    } catch (error) {
      console.error("Error fetching freight zones:", error);
    }
  };
  
  // Calculate price based on period
  const calculatePeriodPrice = (monthlyPrice, periodType) => {
    if (periodType === "month") return monthlyPrice;
    if (periodType === "week") return Math.round(monthlyPrice / 3);
    if (periodType === "day") return Math.round(monthlyPrice / 3 / 5);
    return monthlyPrice;
  };
  
  // Calculate operator days based on period
  // Maquinaria trabaja lunes a sábado (6 días por semana)
  const calculateOperatorDays = (periodType, quantity) => {
    if (periodType === "day") return quantity;
    if (periodType === "week") return quantity * 6;  // Lunes a sábado
    if (periodType === "month") return quantity * 26; // ~4.33 semanas × 6 días
    return quantity;
  };
  
  // Handle client selection
  const handleClientSelect = (clientId) => {
    // Manejar "Público en General"
    if (clientId === "publico_general") {
      setFormData(prev => ({
        ...prev,
        client_id: "",
        client_name: "Público en General",
        client_company: "",
        client_rfc: "XAXX010101000",
        client_email: "",
        client_phone: "",
        client_address: ""
      }));
      return;
    }
    
    const client = clients.find(c => c.id === clientId);
    if (client) {
      // Usar business_name como nombre de empresa y primary_contact para datos de contacto
      const contactName = client.primary_contact?.name || "";
      const contactEmail = client.primary_contact?.email || "";
      const contactPhone = client.primary_contact?.phone || "";
      
      // Formatear dirección
      const addr = client.address || {};
      const addressParts = [
        addr.street,
        addr.exterior_number && `#${addr.exterior_number}`,
        addr.interior_number && `Int. ${addr.interior_number}`,
        addr.neighborhood,
        addr.postal_code && `C.P. ${addr.postal_code}`,
        addr.city,
        addr.state
      ].filter(Boolean);
      const fullAddress = addressParts.join(", ");
      
      setFormData(prev => ({
        ...prev,
        client_id: client.id,
        client_name: contactName || client.business_name || "",
        client_company: client.business_name || "",
        client_rfc: client.rfc || "",
        client_email: contactEmail,
        client_phone: contactPhone,
        client_address: fullAddress
      }));
    }
  };
  
  // Handle vehicle selection for new item
  const handleVehicleSelect = (vehicleId) => {
    const vehicle = rentalVehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      // Find matching operator
      const matchingOperator = operators.find(op => 
        op.equipment_types?.some(type => 
          vehicle.model_name?.toLowerCase().includes(type.toLowerCase()) ||
          type.toLowerCase().includes(vehicle.model_name?.toLowerCase().split(' ')[0])
        )
      );
      
      // El campo puede ser rental_price o rental_price_monthly
      const monthlyPrice = parseFloat(vehicle.rental_price) || parseFloat(vehicle.rental_price_monthly) || 0;
      
      setNewItem(prev => ({
        ...prev,
        vehicle_id: vehicle.id,
        vehicle_code: vehicle.code || "",
        vehicle_name: vehicle.model_name || "",
        vehicle_brand: vehicle.brand || "",
        monthly_price: monthlyPrice,
        operator_id: matchingOperator?.id || "",
        operator_name: matchingOperator?.name || "",
        operator_daily_cost: matchingOperator?.daily_cost || 0
      }));
    }
  };
  
  // Add item to quotation
  const handleAddItem = () => {
    if (!newItem.vehicle_id) {
      toast.error("Selecciona un equipo");
      return;
    }
    
    const unitPrice = calculatePeriodPrice(newItem.monthly_price, newItem.period_type);
    const subtotal = unitPrice * newItem.period_quantity;
    
    let operatorTotal = 0;
    if (newItem.include_operator && newItem.operator_daily_cost > 0) {
      const days = calculateOperatorDays(newItem.period_type, newItem.period_quantity);
      operatorTotal = newItem.operator_daily_cost * days;
    }
    
    // Construir item explícitamente con todos los campos de flete
    const item = {
      vehicle_id: newItem.vehicle_id,
      vehicle_code: newItem.vehicle_code || "",
      vehicle_name: newItem.vehicle_name,
      vehicle_brand: newItem.vehicle_brand,
      monthly_price: newItem.monthly_price,
      period_type: newItem.period_type,
      period_quantity: newItem.period_quantity,
      unit_price: unitPrice,
      subtotal: subtotal,
      // Operador
      include_operator: newItem.include_operator,
      operator_id: newItem.operator_id,
      operator_name: newItem.operator_name,
      operator_daily_cost: newItem.operator_daily_cost,
      operator_total: operatorTotal,
      // Flete - asegurar que se copien correctamente
      include_freight: newItem.include_freight === true,
      freight_zone_id: newItem.freight_zone_id || "",
      freight_zone_name: newItem.freight_zone_name || "",
      freight_cost: parseFloat(newItem.freight_cost) || 0,
      freight_destination: newItem.freight_destination || ""
    };
    
    console.log("Adding item:", item);
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));
    
    // Reset new item form
    setNewItem({
      vehicle_id: "",
      vehicle_code: "",
      vehicle_name: "",
      vehicle_brand: "",
      monthly_price: 0,
      period_type: "month",
      period_quantity: 1,
      include_operator: false,
      operator_id: "",
      operator_name: "",
      operator_daily_cost: 0,
      include_freight: false,
      freight_zone_id: "",
      freight_zone_name: "",
      freight_cost: 0,
      freight_destination: ""
    });
  };
  
  // Remove item from quotation
  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };
  
  // Calculate totals - ahora suma flete de cada item
  const calculateTotals = () => {
    const subtotalEquipment = formData.items.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
    const subtotalOperators = formData.items.reduce((sum, item) => sum + (parseFloat(item.operator_total) || 0), 0);
    const subtotalFreight = formData.items.reduce((sum, item) => {
      if (item.include_freight) {
        const cost = parseFloat(item.freight_cost) || 0;
        console.log("Freight for item:", item.vehicle_name, "include_freight:", item.include_freight, "cost:", cost);
        return sum + cost;
      }
      return sum;
    }, 0);
    const subtotal = subtotalEquipment + subtotalOperators + subtotalFreight;
    const ivaAmount = Math.round(subtotal * 0.16);
    const total = subtotal + ivaAmount;
    
    console.log("Totals calculated:", { subtotalEquipment, subtotalOperators, subtotalFreight, subtotal });
    
    return { subtotalEquipment, subtotalOperators, subtotalFreight, subtotal, ivaAmount, total };
  };
  
  // Save quotation
  const handleSave = async () => {
    if (formData.items.length === 0) {
      toast.error("Agrega al menos un equipo a la cotización");
      return;
    }
    
    if (!formData.client_name) {
      toast.error("Selecciona o ingresa un cliente");
      return;
    }
    
    setSaving(true);
    try {
      const totals = calculateTotals();
      const payload = {
        ...formData,
        business_id: businessId,
        subtotal_equipment: totals.subtotalEquipment,
        subtotal_operators: totals.subtotalOperators,
        subtotal_freight: totals.subtotalFreight,
        subtotal: totals.subtotal,
        iva_amount: totals.ivaAmount,
        total: totals.total
      };
      
      if (editingQuotation) {
        await axios.put(`${API}/api/rental-quotations/${editingQuotation.id}`, payload);
        toast.success("Cotización actualizada");
      } else {
        await axios.post(`${API}/api/rental-quotations`, payload);
        toast.success("Cotización creada");
      }
      
      fetchQuotations();
      resetForm();
    } catch (error) {
      toast.error("Error al guardar cotización");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  // Reset form
  const resetForm = () => {
    setShowForm(false);
    setEditingQuotation(null);
    setFormData({
      client_id: "",
      client_name: "",
      client_company: "",
      client_rfc: "",
      client_email: "",
      client_phone: "",
      client_address: "",
      items: [],
      rental_start_date: "",
      rental_end_date: "",
      validity_days: 15,
      terms: companyConfig?.default_terms_rental || DEFAULT_RENTAL_TERMS,
      notes: "",
      seller_id: "",
      seller_name: ""
    });
  };
  
  // Edit quotation
  const handleEdit = (quotation) => {
    setEditingQuotation(quotation);
    setFormData({
      client_id: quotation.client_id || "",
      client_name: quotation.client_name || "",
      client_company: quotation.client_company || "",
      client_rfc: quotation.client_rfc || "",
      client_email: quotation.client_email || "",
      client_phone: quotation.client_phone || "",
      client_address: quotation.client_address || "",
      items: quotation.items || [],
      include_freight: quotation.include_freight || false,
      freight_zone_id: quotation.freight_zone_id || "",
      freight_zone_name: quotation.freight_zone_name || "",
      freight_cost: quotation.freight_cost || 0,
      freight_destination: quotation.freight_destination || "",
      rental_start_date: quotation.rental_start_date || "",
      rental_end_date: quotation.rental_end_date || "",
      validity_days: quotation.validity_days || 15,
      terms: quotation.terms || "",
      notes: quotation.notes || "",
      seller_id: quotation.seller_id || "",
      seller_name: quotation.seller_name || ""
    });
    setShowForm(true);
  };
  
  // Delete quotation
  const handleDelete = async (quotationId) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    
    try {
      await axios.delete(`${API}/api/rental-quotations/${quotationId}`);
      toast.success("Cotización eliminada");
      fetchQuotations();
    } catch (error) {
      toast.error("Error al eliminar cotización");
    }
  };
  
  // Download PDF
  const handleDownloadPDF = async (quotationId, quotationNumber) => {
    try {
      const response = await axios.get(`${API}/api/rental-quotations/${quotationId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cotizacion_Renta_${quotationNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al generar PDF");
    }
  };
  
  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="rental-quotations-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-7 h-7 text-orange-500" />
            Cotizaciones de Renta
          </h2>
          <p className="text-slate-400 mt-1">Gestiona cotizaciones de renta de equipo</p>
        </div>
        
        {!showForm && (
          <button
            onClick={() => {
              // Cargar términos por defecto desde config si existen
              const defaultTerms = companyConfig?.default_terms_rental || DEFAULT_RENTAL_TERMS;
              setFormData(prev => ({ ...prev, terms: defaultTerms }));
              setShowForm(true);
            }}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
            data-testid="new-rental-quote-btn"
          >
            <Plus className="w-4 h-4" />
            Nueva Cotización
          </button>
        )}
      </div>
      
      {/* Form */}
      {showForm && (
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-xl font-semibold text-white mb-6">
            {editingQuotation ? "Editar Cotización" : "Nueva Cotización de Renta"}
          </h3>
          
          {/* Client Section */}
          <div className="mb-6">
            <h4 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Datos del Cliente
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Buscar Cliente</label>
                <select
                  value={formData.client_id}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option value="" className="bg-slate-800 text-slate-300">-- Seleccionar cliente --</option>
                  <option value="publico_general" className="bg-slate-800 text-green-400">✓ Público en General</option>
                  {clients.map(client => {
                    const displayName = client.business_name || client.primary_contact?.name || "Sin nombre";
                    const contactName = client.primary_contact?.name;
                    return (
                      <option key={client.id} value={client.id} className="bg-slate-800 text-white">
                        {displayName} {contactName && displayName !== contactName ? `(${contactName})` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Empresa</label>
                <input
                  type="text"
                  value={formData.client_company}
                  onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">RFC</label>
                <input
                  type="text"
                  value={formData.client_rfc}
                  onChange={(e) => setFormData({ ...formData, client_rfc: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                />
              </div>
            </div>
          </div>
          
          {/* Equipment Section */}
          <div className="mb-6">
            <h4 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-400" />
              Equipos a Rentar
            </h4>
            
            {/* Add Item Form */}
            <div className="bg-slate-900 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">Equipo</label>
                  <select
                    value={newItem.vehicle_id}
                    onChange={(e) => handleVehicleSelect(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                  >
                    <option value="">-- Seleccionar equipo --</option>
                    {rentalVehicles.map(vehicle => {
                      const price = parseFloat(vehicle.rental_price) || parseFloat(vehicle.rental_price_monthly) || 0;
                      return (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.model_name} - ${price.toLocaleString()}/mes
                        </option>
                      );
                    })}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Periodo</label>
                  <select
                    value={newItem.period_type}
                    onChange={(e) => setNewItem({ ...newItem, period_type: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                  >
                    <option value="day">Día</option>
                    <option value="week">Semana</option>
                    <option value="month">Mes</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={newItem.period_quantity}
                    onChange={(e) => setNewItem({ ...newItem, period_quantity: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Precio Unit.</label>
                  <div className="px-3 py-2 bg-slate-700 rounded-lg text-white text-sm">
                    ${calculatePeriodPrice(newItem.monthly_price, newItem.period_type).toLocaleString()}
                  </div>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={handleAddItem}
                    disabled={!newItem.vehicle_id}
                    className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Plus className="w-4 h-4 inline mr-1" />
                    Agregar
                  </button>
                </div>
              </div>
              
              {/* Operator Option */}
              {newItem.vehicle_id && (
                <div className="mt-3 pt-3 border-t border-slate-700 space-y-3">
                  {/* Operador */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newItem.include_operator}
                      onChange={(e) => setNewItem({ ...newItem, include_operator: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600"
                    />
                    <span className="text-sm text-slate-300">
                      Incluir operador
                      {newItem.operator_name && (
                        <span className="text-slate-500">
                          {" "}({newItem.operator_name} - ${newItem.operator_daily_cost?.toLocaleString()}/día)
                        </span>
                      )}
                    </span>
                  </label>
                  
                  {newItem.include_operator && !newItem.operator_id && (
                    <div className="ml-6">
                      <select
                        value={newItem.operator_id}
                        onChange={(e) => {
                          const op = operators.find(o => o.id === e.target.value);
                          setNewItem({
                            ...newItem,
                            operator_id: op?.id || "",
                            operator_name: op?.name || "",
                            operator_daily_cost: op?.daily_cost || 0
                          });
                        }}
                        className="px-3 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                      >
                        <option value="">-- Seleccionar operador --</option>
                        {operators.map(op => (
                          <option key={op.id} value={op.id}>
                            {op.name} - ${op.daily_cost?.toLocaleString()}/día
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Flete por equipo */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newItem.include_freight}
                      onChange={(e) => setNewItem({ ...newItem, include_freight: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600"
                    />
                    <span className="text-sm text-slate-300">
                      Incluir flete
                      {newItem.freight_zone_name && (
                        <span className="text-slate-500">
                          {" "}({newItem.freight_zone_name} - ${newItem.freight_cost?.toLocaleString()})
                        </span>
                      )}
                    </span>
                  </label>
                  
                  {newItem.include_freight && (
                    <div className="ml-6 grid grid-cols-2 gap-2">
                      <select
                        value={newItem.freight_zone_id}
                        onChange={(e) => {
                          const zone = freightZones.find(z => z.id === e.target.value);
                          setNewItem({
                            ...newItem,
                            freight_zone_id: zone?.id || "",
                            freight_zone_name: zone?.name || "",
                            freight_cost: zone?.round_trip_cost || 0
                          });
                        }}
                        className="px-3 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                      >
                        <option value="">-- Zona de flete --</option>
                        {freightZones.map(zone => (
                          <option key={zone.id} value={zone.id}>
                            {zone.name} - ${zone.round_trip_cost?.toLocaleString()}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Destino específico"
                        value={newItem.freight_destination}
                        onChange={(e) => setNewItem({ ...newItem, freight_destination: e.target.value })}
                        className="px-3 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Items List */}
            {formData.items.length > 0 && (
              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700">
                    <tr>
                      <th className="px-4 py-2 text-left text-slate-300">Equipo</th>
                      <th className="px-4 py-2 text-center text-slate-300">Periodo</th>
                      <th className="px-4 py-2 text-center text-slate-300">Cant.</th>
                      <th className="px-4 py-2 text-right text-slate-300">Precio Unit.</th>
                      <th className="px-4 py-2 text-right text-slate-300">Subtotal</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, idx) => (
                      <React.Fragment key={idx}>
                        <tr className="border-t border-slate-700">
                          <td className="px-4 py-3">
                            <span className="text-white font-medium">{item.vehicle_name}</span>
                            <span className="text-slate-500 text-xs block">{item.vehicle_brand}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-slate-300">{PERIOD_NAMES[item.period_type]}</td>
                          <td className="px-4 py-3 text-center text-slate-300">{item.period_quantity}</td>
                          <td className="px-4 py-3 text-right text-slate-300">${item.unit_price?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-white font-medium">${item.subtotal?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleRemoveItem(idx)}
                              className="p-1 text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                        {item.include_operator && item.operator_total > 0 && (
                          <tr className="bg-slate-800/50">
                            <td className="px-4 py-2 pl-8 text-slate-400 text-xs" colSpan={3}>
                              → Operador: {item.operator_name}
                            </td>
                            <td className="px-4 py-2 text-right text-slate-400 text-xs">
                              ${item.operator_daily_cost?.toLocaleString()}/día
                            </td>
                            <td className="px-4 py-2 text-right text-slate-300">
                              ${item.operator_total?.toLocaleString()}
                            </td>
                            <td></td>
                          </tr>
                        )}
                        {item.include_freight && item.freight_cost > 0 && (
                          <tr className="bg-slate-800/50">
                            <td className="px-4 py-2 pl-8 text-slate-400 text-xs" colSpan={3}>
                              → Flete: {item.freight_zone_name} {item.freight_destination && `(${item.freight_destination})`}
                            </td>
                            <td className="px-4 py-2 text-right text-slate-400 text-xs">
                              Ida y vuelta
                            </td>
                            <td className="px-4 py-2 text-right text-slate-300">
                              ${item.freight_cost?.toLocaleString()}
                            </td>
                            <td></td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Dates & Notes */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Fecha Inicio Renta</label>
              <input
                type="date"
                value={formData.rental_start_date}
                onChange={(e) => setFormData({ ...formData, rental_start_date: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Fecha Fin Renta</label>
              <input
                type="date"
                value={formData.rental_end_date}
                onChange={(e) => setFormData({ ...formData, rental_end_date: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Vigencia (días)</label>
              <input
                type="number"
                value={formData.validity_days}
                onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 15 })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Notas</label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionales"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          {/* Términos y Condiciones - EDITABLES */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-orange-400" />
                Términos y Condiciones (editables para esta cotización)
              </label>
              <button
                type="button"
                onClick={() => {
                  const defaultTerms = companyConfig?.default_terms_rental || DEFAULT_RENTAL_TERMS;
                  setFormData({ ...formData, terms: defaultTerms });
                  toast.success("Términos restaurados al valor por defecto");
                }}
                className="text-xs text-orange-400 hover:text-orange-300 underline"
                data-testid="restore-default-terms-btn"
              >
                Restaurar valor por defecto
              </button>
            </div>
            <textarea
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              rows={10}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm font-mono resize-none focus:border-orange-500 focus:outline-none"
              placeholder="Términos y condiciones específicos para esta cotización..."
              data-testid="rental-quote-terms-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Estos términos se imprimirán en el PDF. Puedes ajustarlos para esta cotización sin afectar la configuración por defecto.
            </p>
          </div>
          
          {/* Totals */}
          <div className="bg-slate-900 rounded-lg p-4 mb-6">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal Equipo:</span>
                  <span>${totals.subtotalEquipment.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal Operadores:</span>
                  <span>${totals.subtotalOperators.toLocaleString()}</span>
                </div>
                {totals.subtotalFreight > 0 && (
                  <div className="flex justify-between text-slate-400">
                    <span>Subtotal Flete:</span>
                    <span>${totals.subtotalFreight.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-white font-medium border-t border-slate-700 pt-2">
                  <span>Subtotal:</span>
                  <span>${totals.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>IVA (16%):</span>
                  <span>${totals.ivaAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-orange-400 border-t border-slate-700 pt-2">
                  <span>TOTAL:</span>
                  <span>${totals.total.toLocaleString()} MXN</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            >
              <X className="w-4 h-4 inline mr-1" />
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 inline mr-1" />
              )}
              {editingQuotation ? "Actualizar" : "Guardar"}
            </button>
          </div>
        </div>
      )}
      
      {/* Quotations List */}
      {!showForm && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              Cargando cotizaciones...
            </div>
          ) : quotations.length === 0 ? (
            <div className="text-center py-12 bg-slate-800 rounded-lg">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No hay cotizaciones de renta</p>
              <p className="text-slate-500 text-sm">Crea tu primera cotización de renta de equipo</p>
            </div>
          ) : (
            quotations.map(quotation => {
              const StatusIcon = STATUS_CONFIG[quotation.status]?.icon || FileText;
              const isExpanded = expandedQuotation === quotation.id;
              
              return (
                <div
                  key={quotation.id}
                  className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden"
                >
                  {/* Header */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-750"
                    onClick={() => setExpandedQuotation(isExpanded ? null : quotation.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${STATUS_CONFIG[quotation.status]?.color || 'bg-slate-600'}`}>
                        <StatusIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">
                          {quotation.quotation_number}
                        </h4>
                        <p className="text-slate-400 text-sm">
                          {quotation.client_name} {quotation.client_company && `(${quotation.client_company})`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-orange-400">
                          ${quotation.total?.toLocaleString()} MXN
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(quotation.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPDF(quotation.id, quotation.quotation_number);
                          }}
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                          title="Descargar PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(quotation);
                          }}
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(quotation.id);
                          }}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-slate-700">
                      <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Equipos:</span>
                          <ul className="mt-1 text-slate-300">
                            {quotation.items?.map((item, idx) => (
                              <li key={idx}>
                                • {item.vehicle_name} ({item.period_quantity} {PERIOD_NAMES[item.period_type]}{item.period_quantity > 1 ? 's' : ''})
                                {item.include_operator && ' + Operador'}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <span className="text-slate-500">Periodo:</span>
                          <p className="text-slate-300">
                            {quotation.rental_start_date || 'Por definir'} - {quotation.rental_end_date || 'Por definir'}
                          </p>
                          {quotation.include_freight && (
                            <>
                              <span className="text-slate-500 mt-2 block">Flete:</span>
                              <p className="text-slate-300">{quotation.freight_zone_name} - ${quotation.freight_cost?.toLocaleString()}</p>
                            </>
                          )}
                        </div>
                        <div>
                          <span className="text-slate-500">Desglose:</span>
                          <p className="text-slate-300">Equipo: ${quotation.subtotal_equipment?.toLocaleString()}</p>
                          <p className="text-slate-300">Operadores: ${quotation.subtotal_operators?.toLocaleString()}</p>
                          <p className="text-slate-300">IVA: ${quotation.iva_amount?.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default RentalQuotationsTab;
